import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowUp, FiArrowDown, FiMinus, FiTable, FiBarChart2, FiArrowRight } from "react-icons/fi";
import { INK, STATUS, areaPath, compact, deltaText, donutArcs, formatDay, linePath, nearestIndex, niceScale, percent, sum, tickIndexes, xAt } from "./chartUtils";

// ---- small shared pieces ----------------------------------------------------------------------------------------------------------------------
function useWidth(ref, fallback = 640) {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => setWidth(Math.max(160, Math.round(node.getBoundingClientRect().width)));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// A card of the dashboard. `accent` is the colour of the thing the card is about (a dot, never text colour).
export function Panel({ title, subtitle, accent, to, linkText = "Open", action, children, className = "", id }) {
  return (
    <section id={id} className={`rounded-2xl bg-navy border border-navy-border p-5 ${className}`}>
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="font-semibold text-[15px] flex items-center gap-2">{accent && <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: accent }} />}<span className="truncate">{title}</span></h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {action}
          {to && <Link to={to} className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1 whitespace-nowrap">{linkText} <FiArrowRight aria-hidden="true" /></Link>}
        </div>
      </header>
      {children}
    </section>
  );
}

// +12% with an arrow (never colour alone). `upIsGood` decides whether a rise is green or red.
export function DeltaBadge({ delta, upIsGood = true }) {
  if (delta === null || delta === undefined) return <span className="text-[11px] text-gray-400">new</span>;
  const up = delta > 0;
  const good = delta === 0 ? null : up === upIsGood;
  const color = good === null ? INK.muted : good ? STATUS.good : STATUS.serious;
  const Icon = delta === 0 ? FiMinus : up ? FiArrowUp : FiArrowDown;
  return <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums" style={{ color }} title="Compared with the period before"><Icon aria-hidden="true" size={12} /><span className="sr-only">{up ? "up" : delta === 0 ? "unchanged" : "down"} </span>{deltaText(delta).replace(/^[+-]/, "")}</span>;
}

export function Sparkline({ values, color, label = "Trend" }) {
  const w = 120;
  const h = 34;
  const pad = 4;
  const list = values && values.length ? values : [0];
  const max = Math.max(1, ...list);
  const pts = list.map((v, i) => [xAt(i, list.length, pad, w - pad), h - pad - (v / max) * (h - pad * 2)]);
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-9 block" role="img" aria-label={`${label}: ${list.length} days, latest ${list[list.length - 1]}`}>
      <path d={areaPath(pts, h - pad)} fill={color} opacity="0.1" />
      <path d={linePath(pts)} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill={color} stroke={INK.surface} strokeWidth="2" />
    </svg>
  );
}

// The big numbers at the top: label, value, change since the period before, a trend.
export function StatTile({ label, value, delta, upIsGood = true, hint, series, color, to }) {
  const body = (
    <>
      <div className="text-xs text-gray-300 flex items-center gap-1.5 min-w-0" title={label}><span aria-hidden="true" className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} /><span className="truncate">{label}</span></div>
      <div className="flex items-end justify-between gap-2 mt-3 mb-1">
        <span className="text-[30px] leading-none font-bold">{typeof value === "number" ? compact(value) : value}</span>
        {delta !== undefined && <span className="pb-0.5"><DeltaBadge delta={delta} upIsGood={upIsGood} /></span>}
      </div>
      <div className="text-[11px] text-gray-500 truncate h-4" title={hint}>{hint}</div>
      {series && <div className="mt-2 -mx-1"><Sparkline values={series} color={color} label={label} /></div>}
    </>
  );
  const cls = "block rounded-2xl bg-navy border border-navy-border p-4 transition-colors hover:border-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-light";
  return to ? <Link to={to} className={cls}>{body}</Link> : <div className={cls}>{body}</div>;
}

function TooltipBox({ left, top, boxWidth, children }) {
  const flip = left > boxWidth - 190;
  return (
    <div role="status" className="pointer-events-none absolute z-20 min-w-[150px] rounded-lg border border-navy-border bg-navy-dark/95 px-3 py-2 shadow-xl text-xs" style={{ left: flip ? undefined : left + 14, right: flip ? boxWidth - left + 14 : undefined, top }}>
      {children}
    </div>
  );
}

// ---- the lines: one tooltip for every series at the day the pointer is nearest to --------------------------------------------------------------
export function LineChart({ dates, series, height = 270, unit = "", area = false, title = "Chart", startHidden = [] }) {
  const wrap = useRef(null);
  const width = useWidth(wrap);
  const [hidden, setHidden] = useState(() => Object.fromEntries(startHidden.map((k) => [k, true])));
  const [hover, setHover] = useState(null);
  const [table, setTable] = useState(false);
  const left = 40;
  const right = 16;
  const top = 12;
  const bottom = 28;
  const visible = series.filter((s) => !hidden[s.key]);
  const scale = niceScale(Math.max(1, ...visible.flatMap((s) => s.values)), 4);
  const plotH = height - top - bottom;
  const y = (v) => top + plotH - (v / scale.max) * plotH;
  const count = dates.length;
  const points = (s) => s.values.map((v, i) => [xAt(i, count, left, width - right), y(v)]);
  const labels = tickIndexes(count, width < 520 ? 4 : 6);

  const onMove = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    setHover(nearestIndex(e.clientX - box.left, count, left, width - right));
  };
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); setHover((h) => Math.min(count - 1, (h === null ? count - 1 : h) + 1)); }
    if (e.key === "ArrowLeft") { e.preventDefault(); setHover((h) => Math.max(0, (h === null ? count - 1 : h) - 1)); }
    if (e.key === "Escape") setHover(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        {series.map((s) => (
          <button key={s.key} type="button" aria-pressed={!hidden[s.key]} onClick={() => setHidden((h) => ({ ...h, [s.key]: !h[s.key] }))}
            className={`inline-flex items-center gap-2 text-xs rounded-md px-1.5 py-1 hover:bg-white/5 ${hidden[s.key] ? "opacity-40" : ""}`} title={hidden[s.key] ? "Show this line" : "Hide this line"}>
            <span aria-hidden="true" className="w-4 h-0.5 rounded" style={{ background: s.color }} />
            <span className="text-gray-300">{s.label}</span><span className="text-gray-500 tabular-nums">{compact(sum(s.values))}{unit}</span>
          </button>
        ))}
        <button type="button" onClick={() => setTable((v) => !v)} className="ml-auto text-xs text-gray-400 hover:text-white inline-flex items-center gap-1.5">{table ? <FiBarChart2 aria-hidden="true" /> : <FiTable aria-hidden="true" />}{table ? "View as chart" : "View as table"}</button>
      </div>

      {table ? (
        <div className="max-h-[300px] overflow-auto rounded-lg border border-navy-border">
          <table className="w-full text-xs">
            <caption className="sr-only">{title}</caption>
            <thead className="sticky top-0 bg-navy-dark text-gray-400"><tr><th scope="col" className="text-left px-3 py-2 font-medium">Day</th>{series.map((s) => <th key={s.key} scope="col" className="text-right px-3 py-2 font-medium">{s.label}</th>)}</tr></thead>
            <tbody>{dates.map((d, i) => <tr key={d} className="border-t border-navy-border"><th scope="row" className="text-left px-3 py-1.5 font-normal text-gray-300">{formatDay(d, true)}</th>{series.map((s) => <td key={s.key} className="text-right px-3 py-1.5 tabular-nums">{s.values[i]}</td>)}</tr>)}</tbody>
          </table>
        </div>
      ) : (
        <div ref={wrap} className="relative" style={{ height }}>
          <svg width={width} height={height} className="block select-none" role="img" tabIndex={0} aria-label={`${title}. Use the left and right arrow keys to read each day.`} onKeyDown={onKey} onFocus={() => setHover((h) => (h === null ? count - 1 : h))} onBlur={() => setHover(null)}>
            {scale.ticks.map((t) => (
              <g key={t}>
                <line x1={left} x2={width - right} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth="1" />
                <text x={left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={INK.muted}>{compact(t)}</text>
              </g>
            ))}
            {labels.map((i) => <text key={i} x={xAt(i, count, left, width - right)} y={height - 8} textAnchor={i === 0 ? "start" : i === count - 1 ? "end" : "middle"} fontSize="11" fill={INK.muted}>{formatDay(dates[i])}</text>)}
            {hover !== null && <line x1={xAt(hover, count, left, width - right)} x2={xAt(hover, count, left, width - right)} y1={top} y2={top + plotH} stroke={INK.muted} strokeWidth="1" />}
            {visible.map((s) => {
              const pts = points(s);
              const last = pts[pts.length - 1];
              return (
                <g key={s.key}>
                  {area && visible.length === 1 && <path d={areaPath(pts, top + plotH)} fill={s.color} opacity="0.1" />}
                  <path d={linePath(pts)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  {hover === null && last && <circle cx={last[0]} cy={last[1]} r="4" fill={s.color} stroke={INK.surface} strokeWidth="2" />}
                  {hover !== null && pts[hover] && <circle cx={pts[hover][0]} cy={pts[hover][1]} r="4.5" fill={s.color} stroke={INK.surface} strokeWidth="2" />}
                </g>
              );
            })}
            <rect x={left} y={top} width={Math.max(0, width - left - right)} height={plotH} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
          </svg>
          {hover !== null && (
            <TooltipBox left={xAt(hover, count, left, width - right)} top={top + 4} boxWidth={width}>
              <div className="text-gray-400 mb-1">{formatDay(dates[hover], true)}</div>
              {visible.map((s) => <div key={s.key} className="flex items-center gap-2 py-0.5"><span aria-hidden="true" className="w-3 h-0.5 rounded" style={{ background: s.color }} /><span className="font-semibold tabular-nums text-white">{s.values[hover]}</span><span className="text-gray-400">{s.label}</span></div>)}
            </TooltipBox>
          )}
        </div>
      )}
    </div>
  );
}

// ---- columns: one bar per day, each bar is its own hover target ------------------------------------------------------------------------------
export function Columns({ dates, values, color, label, height = 170, unit = "" }) {
  const wrap = useRef(null);
  const width = useWidth(wrap);
  const [hover, setHover] = useState(null);
  const left = 30;
  const right = 6;
  const top = 8;
  const bottom = 24;
  const scale = niceScale(Math.max(1, ...values), 3);
  const plotH = height - top - bottom;
  const slot = (width - left - right) / dates.length;
  const bar = Math.min(24, Math.max(2, slot - 3));
  const labels = tickIndexes(dates.length, width < 420 ? 3 : 5);
  const y = (v) => top + plotH - (v / scale.max) * plotH;
  return (
    <div ref={wrap} className="relative" style={{ height }}>
      <svg width={width} height={height} className="block" role="img" aria-label={`${label} per day: ${sum(values)} in total`}>
        {scale.ticks.map((t) => <g key={t}><line x1={left} x2={width - right} y1={y(t)} y2={y(t)} stroke={INK.grid} /><text x={left - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill={INK.muted}>{compact(t)}</text></g>)}
        {values.map((v, i) => {
          const h = v > 0 ? Math.max(2, (v / scale.max) * plotH) : 0;
          const x = left + slot * i + (slot - bar) / 2;
          return (
            <g key={dates[i]} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0} aria-label={`${formatDay(dates[i], true)}: ${v} ${label}`}>
              <rect x={left + slot * i} y={top} width={slot} height={plotH} fill="transparent" />
              {h > 0 && <path d={`M${x},${top + plotH}V${top + plotH - h + 4}Q${x},${top + plotH - h} ${x + 4},${top + plotH - h}H${x + bar - 4}Q${x + bar},${top + plotH - h} ${x + bar},${top + plotH - h + 4}V${top + plotH}Z`} fill={color} opacity={hover === null || hover === i ? 1 : 0.55} />}
            </g>
          );
        })}
        {labels.map((i) => <text key={i} x={left + slot * i + slot / 2} y={height - 6} textAnchor={i === 0 ? "start" : i === dates.length - 1 ? "end" : "middle"} fontSize="11" fill={INK.muted}>{formatDay(dates[i])}</text>)}
      </svg>
      {hover !== null && <TooltipBox left={left + slot * hover + slot / 2} top={top} boxWidth={width}><div className="text-gray-400 mb-1">{formatDay(dates[hover], true)}</div><div><span className="font-semibold text-white tabular-nums">{values[hover]}</span> <span className="text-gray-400">{label}{unit}</span></div></TooltipBox>}
    </div>
  );
}

// ---- ranked bars: the value at the tip ----------------------------------------------------------------------------------------------------------------
export function HBars({ rows, color, format = compact, empty = "Nothing yet.", max }) {
  const list = (rows || []).filter((r) => r.count > 0);
  if (!list.length) return <p className="text-sm text-gray-500">{empty}</p>;
  const top = max || Math.max(...list.map((r) => r.count));
  return (
    <ul className="space-y-2.5">
      {list.map((r) => (
        <li key={r.key} className="grid grid-cols-[minmax(0,38%)_1fr_auto] items-center gap-3 text-sm group" title={`${r.label}: ${r.count}`}>
          <span className="truncate text-gray-300" dir="auto">{r.label}</span>
          <span className="h-2 rounded-full bg-white/5"><span className="block h-2 rounded-full transition-[width]" style={{ width: `${Math.max(3, (100 * r.count) / top)}%`, background: r.color || color }} /></span>
          <span className="tabular-nums text-white font-medium text-right min-w-[2ch]">{format(r.count)}</span>
        </li>
      ))}
    </ul>
  );
}

// ---- donut: part of a whole, with its legend ---------------------------------------------------------------------------------------------------------
export function Donut({ slices, center, centerLabel, size = 132, empty = "Nothing yet." }) {
  const [hover, setHover] = useState(null);
  const arcs = useMemo(() => donutArcs(slices), [slices]);
  const total = sum(slices.map((s) => s.count));
  if (!total) return <p className="text-sm text-gray-500">{empty}</p>;
  const active = hover === null ? null : arcs.find((a) => a.key === hover);
  return (
    <div className="flex items-center gap-5 flex-wrap">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg viewBox="0 0 120 120" width={size} height={size} role="img" aria-label={`${centerLabel || "Total"}: ${total}. ${slices.map((s) => `${s.label} ${s.count}`).join(", ")}`}>
          {arcs.map((a) => (
            <path key={a.key} d={a.path} fill={a.color} opacity={hover === null || hover === a.key ? 1 : 0.4} tabIndex={0} aria-label={`${a.label}: ${a.count} (${percent(a.count, total)}%)`}
              onPointerEnter={() => setHover(a.key)} onPointerLeave={() => setHover(null)} onFocus={() => setHover(a.key)} onBlur={() => setHover(null)} />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-xl font-bold leading-none">{compact(active ? active.count : center !== undefined ? center : total)}</span>
          <span className="text-[10px] text-gray-400 mt-1 max-w-[70px] text-center truncate">{active ? active.label : centerLabel}</span>
        </div>
      </div>
      <ul className="flex-1 min-w-[140px] space-y-1.5 text-sm">
        {slices.filter((s) => s.count > 0).map((s) => (
          <li key={s.key} className={`flex items-center gap-2 rounded px-1 ${hover === s.key ? "bg-white/5" : ""}`} onPointerEnter={() => setHover(s.key)} onPointerLeave={() => setHover(null)}>
            <span aria-hidden="true" className="w-3 h-3 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-gray-300 truncate" dir="auto">{s.label}</span>
            <span className="ml-auto tabular-nums text-white font-medium">{s.count}</span>
            <span className="tabular-nums text-gray-500 text-xs w-9 text-right">{percent(s.count, total)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- one stacked bar with a 2px gap between its parts ---------------------------------------------------------------------------------------------
export function StackBar({ parts, empty = "Nothing yet.", legend = true }) {
  const list = (parts || []).filter((p) => p.count > 0);
  const total = sum(list.map((p) => p.count));
  if (!total) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <div>
      <div className="flex gap-[2px] h-3 rounded-md overflow-hidden" role="img" aria-label={list.map((p) => `${p.label} ${p.count}`).join(", ")}>
        {list.map((p) => <span key={p.key} className="block h-3 first:rounded-l-md last:rounded-r-md" style={{ flex: p.count, background: p.color, minWidth: 3 }} title={`${p.label}: ${p.count} (${percent(p.count, total)}%)`} />)}
      </div>
      {legend && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs">
          {list.map((p) => <li key={p.key} className="flex items-center gap-1.5">{p.icon}<span aria-hidden="true" className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} /><span className="text-gray-300" dir="auto">{p.label}</span><span className="text-white tabular-nums font-medium">{p.count}</span><span className="text-gray-500">{percent(p.count, total)}%</span></li>)}
        </ul>
      )}
    </div>
  );
}

// A value against a limit: the track is a lighter step of the same colour.
export function Meter({ label, value, max, color, right, format = compact }) {
  const ratio = max > 0 ? Math.min(1, value / max) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm mb-1.5"><span className="text-gray-300">{label}</span><span className="tabular-nums text-white font-medium">{format(value)}{right ? <span className="text-gray-500 font-normal"> {right}</span> : null}</span></div>
      <div className="h-2 rounded-full" style={{ background: `${color}33` }} role="meter" aria-label={label} aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}><div className="h-2 rounded-full" style={{ width: `${Math.max(ratio ? 2 : 0, ratio * 100)}%`, background: color }} /></div>
    </div>
  );
}

// A plain number with a label, for the small facts of a section.
export function Fact({ label, value, hint, color }) {
  return (
    <div className="rounded-xl bg-white/5 px-3.5 py-3 min-w-0">
      <div className="text-[11px] text-gray-400 flex items-center gap-1.5 truncate">{color && <span aria-hidden="true" className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />}{label}</div>
      <div className="text-xl font-bold mt-1 tabular-nums">{typeof value === "number" ? compact(value) : value}</div>
      {hint && <div className="text-[11px] text-gray-500 truncate">{hint}</div>}
    </div>
  );
}

export function SubTitle({ children }) {
  return <h4 className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-3">{children}</h4>;
}

// Keeps the previous picture on screen, dimmed, while new data loads.
export function Refreshing({ busy, children }) {
  const style = useCallback(() => ({ opacity: busy ? 0.6 : 1, transition: "opacity .2s" }), [busy]);
  return <div style={style()} aria-busy={busy}>{children}</div>;
}
